import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { shopProducts, shopCategories, warehouses } from '@/mocks/shopProducts';

// Mock reseller dashboard data
const resellerStats = [
  { platform: '抖音 Douyin', icon: 'ri-tiktok-line', color: 'bg-black', sales: 128, commission: 342.50, currency: 'USD' },
  { platform: '微信视频号', icon: 'ri-wechat-line', color: 'bg-green-600', sales: 87, commission: 215.80, currency: 'USD' },
  { platform: 'TikTok', icon: 'ri-tiktok-fill', color: 'bg-gray-900', sales: 203, commission: 589.20, currency: 'USD' },
  { platform: 'YouTube', icon: 'ri-youtube-line', color: 'bg-red-600', sales: 56, commission: 178.40, currency: 'USD' },
];

const commissionProducts = [
  { name: '有机棉婴儿连体衣', factory: '广州优棉工厂', commission: '8%', price: '$18.90', sold: 342, warehouse: '🇺🇸 纽约仓', image: 'https://public.readdy.ai/ai/img_res/a17da556b58b988f57b5c22d1a26032e.jpg' },
  { name: '儿童益智积木套装', factory: '深圳玩具厂', commission: '12%', price: '$24.50', sold: 218, warehouse: '🇦🇺 悉尼仓', image: 'https://readdy.ai/api/search-image?query=colorful%20wooden%20building%20blocks%20toy%20set%20for%20children%20minimal%20white%20background%20product%20photography&width=80&height=80&seq=cp-02&orientation=squarish' },
  { name: '真丝旗袍改良版', factory: '苏州丝绸厂', commission: '15%', price: '$89.00', sold: 95, warehouse: '🇬🇧 伦敦仓', image: 'https://readdy.ai/api/search-image?query=elegant%20silk%20qipao%20cheongsam%20dress%20modern%20style%20minimal%20clean%20background%20product%20photography&width=80&height=80&seq=cp-03&orientation=squarish' },
  { name: '汉字练习毛笔套装', factory: '宣城文房厂', commission: '10%', price: '$32.00', sold: 167, warehouse: '🇸🇬 新加坡仓', image: 'https://readdy.ai/api/search-image?query=chinese%20calligraphy%20brush%20set%20ink%20stone%20practice%20paper%20minimal%20white%20background%20product%20photography&width=80&height=80&seq=cp-04&orientation=squarish' },
];

interface ResellerFormData {
  name: string;
  email: string;
  paypalEmail: string;
  platform: string;
  profileUrl: string;
  followers: string;
  country: string;
  introduction: string;
}

export default function ShopSection() {
  const { ref, isVisible } = useScrollAnimation();
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeWarehouse, setActiveWarehouse] = useState('all');
  const [addedToCart, setAddedToCart] = useState<string | null>(null);
  const [resellerTab, setResellerTab] = useState<'how' | 'dashboard' | 'products'>('how');
  const [showRegModal, setShowRegModal] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState<ResellerFormData>({
    name: '', email: '', paypalEmail: '', platform: '', profileUrl: '', followers: '', country: '', introduction: '',
  });

  const filtered = shopProducts.filter((p) => {
    const catMatch = activeCategory === 'all' || p.category === activeCategory;
    const whMatch = activeWarehouse === 'all' || p.warehouseCity === activeWarehouse;
    return catMatch && whMatch;
  });

  const handleAddToCart = (id: string) => {
    setAddedToCart(id);
    setTimeout(() => setAddedToCart(null), 1800);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('submitting');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d7d8jeum3fsfopf23o3g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <section className="py-20 bg-white" ref={ref}>
      <div
        className="max-w-7xl mx-auto px-4 md:px-8 transition-all duration-700"
        style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(30px)' }}
      >
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-full px-5 py-2 mb-4">
            <i className="ri-store-2-line text-rose-500"></i>
            <span className="text-sm font-bold text-rose-700">本地直邮 · 全球仓储</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
            精选好物 · 本地发货
          </h2>
          <p className="text-gray-400 text-sm max-w-xl mx-auto leading-relaxed">
            精选优质服装、婴儿用品、书籍文具，多城市本地仓储直邮，快速送达您手中
          </p>

          {/* Warehouse badges */}
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            {warehouses.map((w) => (
              <span key={w.city} className="inline-flex items-center gap-1 bg-gray-50 border border-gray-100 rounded-full px-3 py-1 text-xs text-gray-500">
                <span>{w.flag}</span>
                <span>{w.label}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-2">
            {shopCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.key
                    ? 'bg-rose-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.label}
              </button>
            ))}
          </div>

          {/* Warehouse Filter */}
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <button
              onClick={() => setActiveWarehouse('all')}
              className={`px-3 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeWarehouse === 'all' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              全部仓库
            </button>
            {warehouses.map((w) => (
              <button
                key={w.city}
                onClick={() => setActiveWarehouse(w.label)}
                className={`px-3 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeWarehouse === w.label ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {w.flag} {w.city}
              </button>
            ))}
          </div>
        </div>

        {/* Product Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-store-2-line text-4xl mb-3 block"></i>
            <p className="text-sm">该仓库暂无此类商品，请切换筛选条件</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {filtered.map((product) => (
              <div
                key={product.id}
                className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300 group"
              >
                {/* Image */}
                <div className="relative w-full h-48 overflow-hidden bg-gray-50">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {product.badge && (
                    <div className="absolute top-2 left-2 bg-rose-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {product.badge}
                    </div>
                  )}
                  {/* Commission badge — bottom left */}
                  {product.commission && (
                    <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-amber-400 text-gray-900 text-xs font-bold px-2 py-0.5 rounded-full">
                      <i className="ri-percent-line text-xs"></i>
                      {product.commission} 佣金
                    </div>
                  )}
                  {/* Warehouse badge */}
                  <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-0.5 text-xs text-gray-600 flex items-center gap-1">
                    <span>{product.warehouseFlag}</span>
                    <span>{product.warehouseCity}</span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="text-xs text-gray-400 mb-1">{product.categoryLabel}</div>
                  <h3 className="text-sm font-semibold text-gray-800 mb-1 leading-snug">{product.name}</h3>
                  <p className="text-xs text-gray-400 mb-2">{product.nameEn}</p>

                  {/* Rating */}
                  <div className="flex items-center gap-1 mb-3">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map((s) => (
                        <i key={s} className={`ri-star-fill text-xs ${s <= Math.round(product.rating) ? 'text-amber-400' : 'text-gray-200'}`}></i>
                      ))}
                    </div>
                    <span className="text-xs text-gray-400">{product.rating}</span>
                    <span className="text-xs text-gray-300 ml-1">已售{product.sold.toLocaleString()}</span>
                  </div>

                  {/* Price + Cart */}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-lg font-bold text-rose-600">{product.currency}{product.price}</span>
                      {product.originalPrice && (
                        <span className="text-xs text-gray-300 line-through ml-1">{product.currency}{product.originalPrice}</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleAddToCart(product.id)}
                      className={`w-8 h-8 flex items-center justify-center rounded-full transition-all cursor-pointer ${
                        addedToCart === product.id
                          ? 'bg-green-500 text-white'
                          : 'bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white'
                      }`}
                    >
                      <i className={`text-sm ${addedToCart === product.id ? 'ri-check-line' : 'ri-shopping-cart-line'}`}></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Reseller / Influencer Commission Section ── */}
        <div className="mt-14">
          {/* Section header */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-3">
                <i className="ri-vip-crown-line text-amber-500 text-sm"></i>
                <span className="text-xs font-bold text-amber-700 tracking-wide">达人分销计划 · Influencer Reseller Program</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800 leading-tight" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                成为达人分销商，轻松赚取佣金
              </h3>
              <p className="text-gray-500 text-sm mt-2 max-w-2xl leading-relaxed">
                无需囤货，无需物流。工厂自定义佣金比例，您分享链接即可赚钱。
                通过 <strong className="text-blue-600">PayPal</strong> 安全收款，全球达人均可参与。
              </p>

              {/* Platform icons */}
              <div className="flex flex-wrap gap-3 mt-4">
                {[
                  { icon: 'ri-tiktok-line', label: '抖音', bg: 'bg-black text-white' },
                  { icon: 'ri-wechat-line', label: '视频号', bg: 'bg-green-600 text-white' },
                  { icon: 'ri-tiktok-fill', label: 'TikTok', bg: 'bg-gray-800 text-white' },
                  { icon: 'ri-youtube-line', label: 'YouTube', bg: 'bg-red-600 text-white' },
                  { icon: 'ri-instagram-line', label: 'Instagram', bg: 'bg-pink-600 text-white' },
                ].map((p, i) => (
                  <div key={i} className={`flex items-center gap-1.5 ${p.bg} rounded-full px-3 py-1.5`}>
                    <i className={`${p.icon} text-sm`}></i>
                    <span className="text-xs font-medium whitespace-nowrap">{p.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Tab switcher */}
            <div className="flex gap-1 bg-gray-100 rounded-full p-1 w-fit mb-6">
              {([
                { key: 'how', label: '如何参与', icon: 'ri-question-line' },
                { key: 'dashboard', label: '我的佣金', icon: 'ri-bar-chart-line' },
                { key: 'products', label: '佣金商品', icon: 'ri-price-tag-3-line' },
              ] as { key: 'how' | 'dashboard' | 'products'; label: string; icon: string }[]).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setResellerTab(tab.key)}
                  className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    resellerTab === tab.key ? 'bg-white text-gray-800' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab: How it works */}
          {resellerTab === 'how' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Steps */}
              <div className="bg-gradient-to-br from-rose-50 to-amber-50 border border-rose-100 rounded-2xl p-6 md:p-8">
                <p className="text-sm font-bold text-gray-700 mb-5">四步开始赚佣金</p>
                <div className="space-y-4">
                  {[
                    { step: '01', icon: 'ri-user-star-line', title: '注册达人账号', desc: '填写您的社交媒体主页，审核通过即可开始' },
                    { step: '02', icon: 'ri-links-line', title: '获取专属分销链接', desc: '每个商品都有独立追踪链接，精准统计您的销售' },
                    { step: '03', icon: 'ri-share-forward-line', title: '在平台发布内容', desc: '抖音、TikTok、YouTube、微信视频号均可分享' },
                    { step: '04', icon: 'ri-paypal-line', title: 'PayPal 收取佣金', desc: '每笔成交后自动结算，PayPal 安全到账，无门槛提现' },
                  ].map((s) => (
                    <div key={s.step} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-rose-100 border border-rose-200 flex-shrink-0">
                        <i className={`${s.icon} text-rose-600 text-base`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{s.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key features */}
              <div className="space-y-4">
                {/* PayPal highlight */}
                <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-blue-600 flex-shrink-0">
                    <i className="ri-paypal-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800 text-sm">PayPal 安全收款</p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                      全球通用，支持 200+ 国家地区。佣金自动结算至您的 PayPal 账户，
                      无最低提现门槛，随时可提。
                    </p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2.5 py-0.5 whitespace-nowrap">自动结算</span>
                      <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2.5 py-0.5 whitespace-nowrap">无门槛提现</span>
                      <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-2.5 py-0.5 whitespace-nowrap">200+ 国家</span>
                    </div>
                  </div>
                </div>

                {/* Commission model */}
                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
                  <p className="font-bold text-gray-800 text-sm mb-3">
                    <i className="ri-percent-line text-amber-500 mr-1.5"></i>
                    工厂自定义佣金比例
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3">
                    每个工厂/供应商根据产品利润自行设定佣金比例，达人可在商品页面查看具体佣金率，选择高佣金商品重点推广。
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: '服装类', range: '8–15%', color: 'text-rose-600' },
                      { label: '母婴类', range: '5–12%', color: 'text-green-600' },
                      { label: '文具类', range: '10–18%', color: 'text-amber-600' },
                    ].map((c) => (
                      <div key={c.label} className="bg-white rounded-xl p-3 text-center border border-amber-100">
                        <p className={`text-base font-bold ${c.color}`}>{c.range}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{c.label}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Fulfillment */}
                <div className="bg-gray-50 border border-gray-100 rounded-2xl p-5">
                  <p className="font-bold text-gray-800 text-sm mb-3">
                    <i className="ri-truck-line text-gray-500 mr-1.5"></i>
                    三种发货模式，全程无需您操心
                  </p>
                  <div className="space-y-2">
                    {[
                      { icon: 'ri-building-2-line', label: '工厂直发', desc: '工厂直接发货至买家，成本最低' },
                      { icon: 'ri-global-line', label: '海外仓发货', desc: '海外仓库就近发货，速度更快' },
                      { icon: 'ri-map-pin-line', label: '本地仓直邮', desc: '本地仓储当日/次日达' },
                    ].map((f) => (
                      <div key={f.label} className="flex items-center gap-3">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-gray-200 flex-shrink-0">
                          <i className={`${f.icon} text-gray-500 text-sm`}></i>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-700">{f.label}</span>
                          <span className="text-xs text-gray-400 ml-2">{f.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tab: My Commission Dashboard */}
          {resellerTab === 'dashboard' && (
            <div className="space-y-5">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: '本月总销售', value: '474', unit: '单', icon: 'ri-shopping-bag-line', color: 'text-rose-600', bg: 'bg-rose-50' },
                  { label: '本月佣金', value: '$1,325.90', unit: 'USD', icon: 'ri-money-dollar-circle-line', color: 'text-amber-600', bg: 'bg-amber-50' },
                  { label: '待结算', value: '$289.40', unit: 'USD', icon: 'ri-time-line', color: 'text-blue-600', bg: 'bg-blue-50' },
                  { label: 'PayPal 已到账', value: '$1,036.50', unit: 'USD', icon: 'ri-paypal-line', color: 'text-green-600', bg: 'bg-green-50' },
                ].map((s) => (
                  <div key={s.label} className={`${s.bg} rounded-2xl p-5 border border-gray-100`}>
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${s.bg} mb-3`}>
                      <i className={`${s.icon} ${s.color} text-lg`}></i>
                    </div>
                    <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{s.label} · {s.unit}</p>
                  </div>
                ))}
              </div>

              {/* Per-platform breakdown */}
              <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6">
                <p className="text-sm font-bold text-gray-700 mb-4">各平台销售 &amp; 佣金明细</p>
                <div className="space-y-3">
                  {resellerStats.map((r) => (
                    <div key={r.platform} className="flex items-center gap-4 bg-white rounded-xl px-4 py-3 border border-gray-100">
                      <div className={`w-9 h-9 flex items-center justify-center rounded-full ${r.color} flex-shrink-0`}>
                        <i className={`${r.icon} text-white text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{r.platform}</p>
                        <p className="text-xs text-gray-400">已售 <strong className="text-gray-600">{r.sales}</strong> 件</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-amber-600">${r.commission.toFixed(2)}</p>
                        <p className="text-xs text-gray-400">佣金收入</p>
                      </div>
                      <div className="w-24 bg-gray-100 rounded-full h-1.5 flex-shrink-0">
                        <div
                          className="bg-amber-400 h-1.5 rounded-full"
                          style={{ width: `${Math.min((r.commission / 600) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-xs text-gray-400">佣金通过 PayPal 自动结算，每周五到账</p>
                  <button className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-blue-700 transition-all">
                    <i className="ri-paypal-line"></i>提现至 PayPal
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Commission Products */}
          {resellerTab === 'products' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-400 mb-4">以下商品由各工厂自定义佣金比例，选择高佣金商品重点推广效果更佳</p>
              {commissionProducts.map((p, i) => (
                <div key={i} className="flex items-center gap-4 bg-gray-50 border border-gray-100 rounded-2xl p-4 hover:border-amber-200 transition-all">
                  <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-white border border-gray-100">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{p.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{p.factory} · {p.warehouse}</p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-xs text-gray-500">售价 <strong className="text-gray-700">{p.price}</strong></span>
                      <span className="text-xs text-gray-300">·</span>
                      <span className="text-xs text-gray-500">已售 <strong className="text-gray-700">{p.sold}</strong> 件</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 text-center">
                    <div className="bg-amber-100 border border-amber-200 rounded-xl px-4 py-2">
                      <p className="text-xl font-bold text-amber-600">{p.commission}</p>
                      <p className="text-xs text-amber-500">佣金比例</p>
                    </div>
                  </div>
                  <button className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-rose-600 text-white rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-rose-700 transition-all">
                    <i className="ri-links-line"></i>获取链接
                  </button>
                </div>
              ))}
              <div className="text-center pt-4">
                <button className="flex items-center gap-2 px-6 py-2.5 border border-gray-200 text-gray-600 rounded-full text-sm font-medium cursor-pointer hover:bg-gray-50 transition-all mx-auto whitespace-nowrap">
                  <i className="ri-arrow-down-line"></i>查看更多佣金商品
                </button>
              </div>
            </div>
          )}

          {/* CTA bar */}
          <div className="mt-8 bg-gradient-to-r from-rose-600 to-amber-500 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-white font-bold text-lg">立即加入达人分销计划</p>
              <p className="text-white/80 text-sm mt-1">无需囤货 · 工厂发货 · PayPal 收款 · 全球达人均可参与</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => setShowRegModal(true)}
                className="flex items-center gap-2 px-7 py-3 bg-white text-rose-600 rounded-full font-bold text-sm cursor-pointer whitespace-nowrap hover:bg-rose-50 transition-all"
              >
                <i className="ri-user-star-line"></i>立即申请
              </button>
              <button className="flex items-center gap-2 px-6 py-3 bg-white/20 border border-white/40 text-white rounded-full font-semibold text-sm cursor-pointer whitespace-nowrap hover:bg-white/30 transition-all">
                <i className="ri-paypal-line"></i>绑定 PayPal
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Banner */}
        <div className="mt-12 bg-gradient-to-r from-rose-50 to-amber-50 border border-rose-100 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-2" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                本地仓储 · 快速直邮
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed max-w-lg">
                我们在全球8大城市设有本地仓储，包括上海、北京、香港、新加坡、悉尼、迪拜、伦敦、纽约，
                就近发货，快速送达，让您购物更便捷。
              </p>
              <div className="flex flex-wrap gap-3 mt-4">
                {[
                  { icon: 'ri-truck-line', text: '本地仓储发货' },
                  { icon: 'ri-shield-check-line', text: '正品保障' },
                  { icon: 'ri-refresh-line', text: '7天无理由退换' },
                  { icon: 'ri-customer-service-line', text: '多语言客服' },
                ].map((item, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs text-gray-600 bg-white rounded-full px-3 py-1.5 border border-gray-100">
                    <i className={`${item.icon} text-rose-500`}></i>
                    {item.text}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 flex flex-col sm:flex-row gap-3">
              <Link
                to="/warehouse"
                className="flex items-center justify-center gap-2 px-8 py-3 bg-rose-600 text-white rounded-full font-semibold text-sm hover:bg-rose-700 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-store-2-line"></i>进入本地仓储市场
              </Link>
              <Link
                to="/warehouse"
                className="flex items-center justify-center gap-2 px-6 py-3 border border-rose-300 text-rose-600 rounded-full font-semibold text-sm hover:bg-rose-50 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-add-line"></i>供应商注册
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── Reseller Registration Modal ── */}
      {showRegModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setShowRegModal(false); setSubmitStatus('idle'); }} />
          <div className="relative bg-white rounded-3xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal header */}
            <div className="sticky top-0 bg-white rounded-t-3xl px-6 pt-6 pb-4 border-b border-gray-100 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-vip-crown-line text-amber-500"></i>
                    <span className="text-xs font-bold text-amber-600">达人分销计划</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                    申请成为分销达人
                  </h3>
                </div>
                <button
                  onClick={() => { setShowRegModal(false); setSubmitStatus('idle'); }}
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-all cursor-pointer"
                >
                  <i className="ri-close-line text-gray-500"></i>
                </button>
              </div>
            </div>

            {submitStatus === 'success' ? (
              <div className="px-6 py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                  <i className="ri-check-line text-green-600 text-2xl"></i>
                </div>
                <h4 className="text-lg font-bold text-gray-800 mb-2">申请已提交！</h4>
                <p className="text-sm text-gray-500 leading-relaxed mb-6">
                  我们将在 1–3 个工作日内审核您的申请，审核结果将发送至您的邮箱。
                  通过后即可获取专属分销链接，开始赚取佣金！
                </p>
                <button
                  onClick={() => { setShowRegModal(false); setSubmitStatus('idle'); }}
                  className="px-8 py-3 bg-rose-600 text-white rounded-full font-semibold text-sm cursor-pointer hover:bg-rose-700 transition-all whitespace-nowrap"
                >
                  好的，期待审核结果
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="reseller-registration-form"
                onSubmit={handleFormSubmit}
                className="px-6 py-5 space-y-4"
              >
                {/* Name + Email */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">真实姓名 *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleFormChange}
                      placeholder="您的真实姓名"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-rose-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">联系邮箱 *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="your@email.com"
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-rose-400 transition-all"
                    />
                  </div>
                </div>

                {/* PayPal email */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    <i className="ri-paypal-line text-blue-500 mr-1"></i>
                    PayPal 收款邮箱 *
                  </label>
                  <input
                    type="email"
                    name="paypalEmail"
                    required
                    value={formData.paypalEmail}
                    onChange={handleFormChange}
                    placeholder="您的 PayPal 邮箱地址"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-blue-400 transition-all"
                  />
                  <p className="text-xs text-gray-400 mt-1">佣金将自动结算至此 PayPal 账户</p>
                </div>

                {/* Platform + Followers */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">主要平台 *</label>
                    <select
                      name="platform"
                      required
                      value={formData.platform}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-rose-400 transition-all cursor-pointer bg-white"
                    >
                      <option value="">请选择平台</option>
                      <option value="douyin">抖音 Douyin</option>
                      <option value="wechat">微信视频号</option>
                      <option value="tiktok">TikTok</option>
                      <option value="youtube">YouTube</option>
                      <option value="instagram">Instagram</option>
                      <option value="other">其他平台</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">粉丝数量 *</label>
                    <select
                      name="followers"
                      required
                      value={formData.followers}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-rose-400 transition-all cursor-pointer bg-white"
                    >
                      <option value="">请选择范围</option>
                      <option value="1k-10k">1K – 10K</option>
                      <option value="10k-50k">10K – 50K</option>
                      <option value="50k-100k">50K – 100K</option>
                      <option value="100k-500k">100K – 500K</option>
                      <option value="500k+">500K 以上</option>
                    </select>
                  </div>
                </div>

                {/* Profile URL */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">社交媒体主页链接 *</label>
                  <input
                    type="url"
                    name="profileUrl"
                    required
                    value={formData.profileUrl}
                    onChange={handleFormChange}
                    placeholder="https://www.tiktok.com/@yourhandle"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-rose-400 transition-all"
                  />
                </div>

                {/* Country */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">所在国家/地区 *</label>
                  <select
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleFormChange}
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-rose-400 transition-all cursor-pointer bg-white"
                  >
                    <option value="">请选择</option>
                    <option value="cn">中国大陆</option>
                    <option value="hk">香港</option>
                    <option value="tw">台湾</option>
                    <option value="sg">新加坡</option>
                    <option value="au">澳大利亚</option>
                    <option value="us">美国</option>
                    <option value="uk">英国</option>
                    <option value="ca">加拿大</option>
                    <option value="other">其他</option>
                  </select>
                </div>

                {/* Introduction */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">自我介绍 / 内容方向</label>
                  <textarea
                    name="introduction"
                    value={formData.introduction}
                    onChange={handleFormChange}
                    maxLength={500}
                    rows={3}
                    placeholder="简单介绍您的内容风格、受众群体、擅长推广的品类等（选填，最多500字）"
                    className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-rose-400 transition-all resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-0.5">{formData.introduction.length}/500</p>
                </div>

                {submitStatus === 'error' && (
                  <p className="text-xs text-red-500 text-center">提交失败，请稍后重试</p>
                )}

                <button
                  type="submit"
                  disabled={submitStatus === 'submitting'}
                  className="w-full py-3 bg-gradient-to-r from-rose-600 to-amber-500 text-white rounded-xl font-bold text-sm cursor-pointer hover:opacity-90 transition-all disabled:opacity-60 whitespace-nowrap"
                >
                  {submitStatus === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>提交中...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-send-plane-line"></i>提交申请
                    </span>
                  )}
                </button>

                <p className="text-xs text-gray-400 text-center leading-relaxed">
                  提交即表示您同意我们的分销合作条款。审核通过后将通过邮件通知您。
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
